import './ParticipantPickerList.css';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Cluster,
  ParticipantFace,
  Row,
  Typography,
} from '@src/components';
import { ParticipantTable } from '@src/components/ParticipantTable/ParticipantTable';
import { ParticipantTableRowList } from '@src/components/ParticipantTable/ParticipantTableRowList';
import { useParticipantTableViewport } from '@src/components/ParticipantTable/useFitParticipantPageSize';
import { testIdAttr, testIdFor } from '@src/components/testId';

export interface ParticipantPickerItem {
  key: string;
  username: string;
  picture?: string | null;
  avatar?: string | null;
  frameName?: string | null;
  /** Name already claimed — show a taken badge and optional recovery action. */
  taken?: boolean;
  hidden?: boolean;
}

export interface ParticipantPickerListProps {
  items: ParticipantPickerItem[];
  onPick: (key: string) => void;
  /** Open existing-card recovery without claiming the name again. */
  onRecover?: (key: string) => void;
  /** Optional caps label above the list — e.g. on box recover. */
  label?: string;
  /** Disables claiming names; optional recovery remains available for existing cards. */
  disabled?: boolean;
  testId?: string;
}

/** Name picker rows on top of the shared participant table shell. */
export function ParticipantPickerList({
  items,
  onPick,
  onRecover,
  label,
  disabled = false,
  testId,
}: ParticipantPickerListProps) {
  const { t } = useTranslation();
  const getSearchText = useMemo(
    () => (item: ParticipantPickerItem) => item.username,
    [],
  );
  const { tableRef, ...table } = useParticipantTableViewport({
    items,
    getSearchText,
  });

  return (
    <ParticipantTable
      ref={tableRef}
      label={label}
      showSearch={table.showSearch}
      search={{
        value: table.query,
        onChange: table.setSearch,
        placeholder: t('box.select.search'),
        clearLabel: t('box.organizer.searchClear'),
      }}
      isEmpty={table.isEmpty}
      empty={
        <Typography as="p" role="body/base" {...testIdAttr(testIdFor(testId, 'empty'))}>
          {t('box.organizer.noResults')}
        </Typography>
      }
      pagination={{
        page: table.page,
        pageCount: table.pageCount,
        onChange: table.setPage,
        total: table.filtered.length,
        pageSize: table.pageSize ?? table.filtered.length,
        previousLabel: t('box.organizer.pages.previous'),
        nextLabel: t('box.organizer.pages.next'),
        pageLabel: (value) => t('box.organizer.pages.page', { page: value }),
      }}
      testId={testId}
    >
      <ParticipantTableRowList
        placeholderCount={table.placeholderCount}
        testId={testIdFor(testId, 'list')}
      >
        {table.visible.map((item, index) => (
          <Row
            key={item.key}
            size="large"
            align="center"
            {...testIdAttr(testIdFor(testId, `option-${item.key}`))}
          >
            <Cluster className="participant-picker__row" justify="between" align="center" gap={4} wide wrap={false}>
              <Cluster className="participant-picker__identity" align="center" gap={3} wrap={false}>
                <ParticipantFace
                  card={item}
                  index={(table.page - 1) * (table.pageSize ?? 0) + index}
                />
                <Typography as="p" role="title/sub">
                  {item.hidden ? t('game.people.nameHidden') : item.taken && onRecover ? (
                    <button type="button" className="participant-picker__name" onClick={() => onRecover(item.key)}>
                      {item.username}
                    </button>
                  ) : item.username}
                </Typography>
              </Cluster>
              {item.hidden ? null : item.taken ? (
                <Cluster className="participant-picker__actions" gap={2} justify="end">
                  <Typography as="span" role="label/button-sm" className="participant-picker__status" {...testIdAttr(testIdFor(testId, `taken-${item.key}`))}>
                    {t('box.select.taken')}
                  </Typography>
                  {onRecover && <Button variant="soft" size="small"
                    onClick={() => onRecover(item.key)}
                    testId={testIdFor(testId, `recover-${item.key}`)}>
                    {t('box.select.pick')}
                  </Button>}
                </Cluster>
              ) : (
                <Button
                  variant="soft"
                  size="small"
                  disabled={disabled}
                  onClick={() => onPick(item.key)}
                  testId={testIdFor(testId, `pick-${item.key}`)}
                >
                  {t('box.select.pick')}
                </Button>
              )}
            </Cluster>
          </Row>
        ))}
      </ParticipantTableRowList>
    </ParticipantTable>
  );
}
